import React, { useState } from 'react';
import { ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';

const EXAMPLES = [
  {
    title: 'Rent Review Mechanism',
    original: '"The rent shall be reviewed to the open market rent as determined by the Landlord\'s surveyor, whose decision shall be final and binding."',
    issue: 'One-sided valuation with no tenant input, no independent determination, and no mechanism for dispute. The landlord has unchecked power to set any rent figure.',
    corrected: '"The rent shall be reviewed to the open market rent as agreed between the parties or, in default of agreement, as determined by an independent surveyor appointed in accordance with the RICS Rent Review Guidance."',
  },
  {
    title: 'Break Clause Exercise',
    original: '"The Tenant may determine this lease by giving not less than 6 months\' notice provided all rents have been paid and all covenants performed."',
    issue: 'Condition requiring "all covenants performed" is almost impossible to satisfy in practice. Even a trivial historic breach could invalidate the break notice.',
    corrected: '"The Tenant may determine this lease by giving not less than 6 months\' prior written notice, provided that the principal rent due has been paid and there is no material or subsisting breach of covenant at the break date."',
  },
  {
    title: 'Dilapidations Standard',
    original: '"The Tenant shall keep the Premises in good and substantial repair and condition and shall yield up the Premises in such repair and condition at the end of the term."',
    issue: 'No reference to condition at lease commencement. The tenant could be liable for pre-existing defects and improvements beyond the original state.',
    corrected: '"The Tenant shall keep the Premises in no worse condition than as evidenced by the Schedule of Condition annexed hereto and shall yield up the Premises in accordance with that standard at the end of the term."',
  },
  {
    title: 'Service Charge Cap',
    original: '"The Tenant shall pay a fair proportion of the costs and expenses incurred by the Landlord in providing the services."',
    issue: 'No cap on service charge contributions, no definition of "fair proportion", and no audit rights. Costs can escalate without limit or accountability.',
    corrected: '"The Tenant shall pay a fair and reasonable proportion of the costs of the services, such proportion not to exceed [X]% of the total cost. The Landlord shall provide annual certified accounts and the Tenant shall have the right to audit."',
  },
];

export function CommercialClauseExamples() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Commercial Clause Improvements
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Common problematic commercial lease clauses and professional corrections
          </p>
        </div>

        <div className="space-y-4">
          {EXAMPLES.map((example, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={example.title}
                className="animate-on-scroll bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-elevated transition-shadow duration-300"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${isOpen ? 'bg-emerald-600' : 'bg-gray-100'}`}>
                      <span className={`text-xs font-bold ${isOpen ? 'text-white' : 'text-gray-600'}`}>{i + 1}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900">{example.title}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 animate-slide-down">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="w-4 h-4 text-red-600" />
                            <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Original Clause (Problematic)</span>
                          </div>
                          <p className="text-sm text-red-900 leading-relaxed italic">{example.original}</p>
                        </div>
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Issue</p>
                          <p className="text-sm text-amber-900 leading-relaxed">{example.issue}</p>
                        </div>
                      </div>

                      <div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 h-full">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Corrected Version</span>
                          </div>
                          <p className="text-sm text-emerald-900 leading-relaxed italic mb-4">{example.corrected}</p>
                          <div className="inline-flex items-center gap-1.5 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1">
                            <CheckCircle className="w-3 h-3 text-emerald-700" />
                            <span className="text-xs font-semibold text-emerald-800">Compliant and legally sound</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
